/**
 * Knowledge Base Manage Modal
 *
 * 깔끔하고 효율적인 카드형 레이아웃
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Badge, BadgeColor, Button, Checkbox, Modal, Select, useToast, Notice } from '../ui';
import { knowledgeBaseApi, KnowledgeBase, Document, EMBEDDING_MODELS, authApi, customerApi, Customer } from '../../api';
import { embeddingProviderApi } from '../../api/embeddingProvider';
import { teamApi, Team } from '../../api/teams';
import connectorApi from '../../api/connectors';
import { DatabaseSourceStep } from './steps/DatabaseSourceStep';
import { DbConfig, DEFAULT_DB_CONFIG } from './shared/pipeline.types';
import UploadIcon from '../../assets/icons/common/upload.svg?react';
import TrashIcon from '../../assets/icons/common/trash.svg?react';
import EditIcon from '../../assets/icons/common/edit.svg?react';
import LocalIcon from '../../assets/icons/common/computer.svg?react';
import googleDriveLogo from '../../assets/icons/marketplace/google-drive.svg';
import dropboxLogo from '../../assets/icons/marketplace/dropbox.svg';
import RefreshIcon from '../../assets/icons/common/refresh.svg?react';
import FolderIcon from '../../assets/icons/common/folder.svg?react';
import '../../styles/kbPipeline.css';

interface Props {
  knowledgeBase: KnowledgeBase;
  onClose: () => void;
  onUpdate: () => void;
  /** DB 설정 편집 버튼 클릭 시 호출 — PipelineBuilderModal을 편집 모드로 열기 */
  onEditDbConfig?: (kb: KnowledgeBase) => void;
}

// ─────────────────────────────────────
// 유틸리티 함수
// ─────────────────────────────────────
function getWebConfig(kb: KnowledgeBase) {
  const cfg = kb.pipelineConfig as any;
  return cfg?.sources?.find((s: any) => s.type === 'web') || cfg?.webConfig || null;
}

function getDbConfig(kb: KnowledgeBase) {
  const cfg = kb.pipelineConfig as any;
  return cfg?.sources?.find((s: any) => s.type === 'database') || cfg?.dbConfig || null;
}

function getActiveSources(kb: KnowledgeBase): Array<'file' | 'web' | 'database'> {
  const cfg = kb.pipelineConfig as any;
  if (cfg?.sources?.length > 0) {
    return cfg.sources.map((s: any) => s.type) as Array<'file' | 'web' | 'database'>;
  }
  return [kb.sourceType as 'file' | 'web' | 'database' || 'file'];
}

const formatSize = (b: number) =>
  b < 1024 ? `${b}B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)}KB` : `${(b / (1024 * 1024)).toFixed(1)}MB`;

// 아이콘
const Icons = {
  file: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13 2 13 9 20 9"/></svg>,
  web: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  database: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>,
  refresh: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/></svg>,
  delete: <TrashIcon width={14} height={14} />,
  add: <UploadIcon width={14} height={14} />,
};

const CHUNK_STRATEGIES = [
  { value: 'fixed',     label: '고정 길이' },
  { value: 'paragraph', label: '문단 기반' },
  { value: 'semantic',  label: '의미 기반' },
];

// ─────────────────────────────────────
// 메인 컴포넌트
// ─────────────────────────────────────
export function KnowledgeBaseManageModal({ knowledgeBase: initialKb, onClose, onUpdate }: Props) {
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 상태
  const [kb, setKb] = useState<KnowledgeBase>(initialKb);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState<{
    total: number;
    indexed: number;
    processing: number;
    error: number;
  } | null>(null);
  const syncPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [saving, setSaving] = useState(false);

  // 파일 소스 탭 (로컬/Drive/Dropbox)
  const [fileTab, setFileTab] = useState<'local' | 'drive' | 'dropbox'>('local');
  // Google Drive 상태
  const [driveConnected, setDriveConnected] = useState<boolean | null>(null);
  const [driveAccount, setDriveAccount] = useState('');
  const [driveFolderId, setDriveFolderId] = useState('');
  const [driveFileList, setDriveFileList] = useState<Array<{ id: string; name: string; mimeType: string; size?: number }>>([]);
  const [driveLoading, setDriveLoading] = useState(false);
  const [driveError, setDriveError] = useState('');
  const [driveImporting, setDriveImporting] = useState(false);

  // Dropbox 상태
  const [dropboxConnected, setDropboxConnected] = useState<boolean | null>(null);
  const [dropboxAccount, setDropboxAccount] = useState('');
  const [dropboxFolderPath, setDropboxFolderPath] = useState('');
  const [dropboxFileList, setDropboxFileList] = useState<Array<{ id: string; name: string; path_lower: string; '.tag': string; size?: number; server_modified?: string }>>([]);
  const [dropboxLoading, setDropboxLoading] = useState(false);
  const [dropboxError, setDropboxError] = useState('');
  const [dropboxImporting, setDropboxImporting] = useState(false);

  // 기본 정보
  const [editName, setEditName] = useState(kb.name);
  const [editDesc, setEditDesc] = useState(kb.description || '');
  const [editCustomerId, setEditCustomerId] = useState((kb as any).customerId || '');
  const [editTeamId, setEditTeamId] = useState((kb as any).teamId || '');

  // 고객사/팀 목록 (상위 관리자용)
  const currentUser = authApi.getUser();
  const role = currentUser?.role || '';
  const needsCustomerSelect = ['productAdmin', 'tenantAdmin', 'admin'].includes(role);
  const needsTeamSelect = ['productAdmin', 'tenantAdmin', 'customerAdmin', 'admin'].includes(role);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);

  useEffect(() => {
    if (needsCustomerSelect) {
      customerApi.list().then(setCustomers).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const cId = needsCustomerSelect ? editCustomerId : currentUser?.customerId;
    if (needsTeamSelect && cId) {
      teamApi.listByCustomer(cId).catch(() => [] as Team[]).then(async (allTeams) => {
        const checks = await Promise.all(
          allTeams.map(t => embeddingProviderApi.listAccounts(`team:${t.id}`).catch(() => []))
        );
        setTeams(allTeams.filter((_, i) => checks[i].length > 0));
      });
    } else {
      setTeams([]);
    }
  }, [editCustomerId]);

  // 웹 설정
  const webCfg = getWebConfig(kb);
  const [webUrl, setWebUrl] = useState(webCfg?.url || '');
  const [webDepth, setWebDepth] = useState(webCfg?.depth || 2);
  const [webPageType, setWebPageType] = useState<'query' | 'path'>(webCfg?.pageType || 'query');
  const [webStartPage, setWebStartPage] = useState(String(webCfg?.startPage || 1));
  const [webEndPage, setWebEndPage] = useState(String(webCfg?.endPage || 1));
  const [webPageParam] = useState(webCfg?.pageParam || 'currentPage');

  // 임베딩 모델
  const initEmbedding = (kb.pipelineConfig as any)?.embeddingModelId || kb.embeddingModel || EMBEDDING_MODELS[0].id;
  const [embeddingModelId, setEmbeddingModelId] = useState(initEmbedding);

  // DB 설정 인라인 편집
  const [dbEditOpen, setDbEditOpen] = useState(false);
  const [dbConfig, setDbConfig] = useState<DbConfig>(() => {
    const cfg = kb.pipelineConfig as any;
    const src = cfg?.sources?.find((s: any) => s.type === 'database') || cfg?.dbConfig;
    if (src) {
      return {
        type: src.dbType || src.type || 'mysql',
        host: src.host || '',
        port: src.port || 3306,
        database: src.database || '',
        schema: src.schema || '',
        username: src.username || '',
        password: src.password || '',
        syncSchedule: src.syncSchedule || 'manual',
        tables: (src.tables || []).map((t: any) => ({
          schema: t.schema,
          table: t.table,
          selected: t.selected !== false,
          labelGenerated: t.labelGenerated || false,
          columns: (t.columns || []).map((c: any) => ({
            name: c.name, type: c.type, nullable: c.nullable ?? true,
            comment: c.comment || '', label: c.label || '', include: c.include !== false,
          })),
        })),
        mode: 'text2sql',
        sampleRows: src.sampleRows ?? 3,
        hints: src.hints ?? [],
        sqlSystemPrompt: src.sqlSystemPrompt ?? undefined,
        sqlSummaryPrompt: src.sqlSummaryPrompt ?? undefined,
      };
    }
    return DEFAULT_DB_CONFIG;
  });

  // 청킹 설정
  const isDbSource = getActiveSources(kb).includes('database') && !getActiveSources(kb).includes('file') && !getActiveSources(kb).includes('web');
  const defaultOverlap = isDbSource ? 0 : 50;
  const initChunking = (kb.pipelineConfig as any)?.chunking || { strategy: 'fixed', chunkSize: kb.chunkSize || 500, chunkOverlap: defaultOverlap };
  const [chunkStrategy, setChunkStrategy] = useState<string>(initChunking.strategy || 'fixed');
  const [chunkSize, setChunkSize] = useState<number>(initChunking.chunkSize ?? 500);
  // DB 소스는 오버랩 항상 0 강제 (저장된 값 무시)
  const [chunkOverlap, setChunkOverlap] = useState<number>(isDbSource ? 0 : (initChunking.chunkOverlap ?? defaultOverlap));

  // 검색 설정
  const initSearch = (kb.pipelineConfig as any)?.search || { topK: 5, similarityThreshold: 0.3, enableReranking: false };
  const [topK, setTopK] = useState<number>(initSearch.topK ?? 5);
  const [similarityThreshold, setSimilarityThreshold] = useState<number>(initSearch.similarityThreshold ?? 0.3);
  const [searchType, setSearchType] = useState<'semantic' | 'hybrid'>(initSearch.searchType ?? 'semantic');
  const [enableReranking, setEnableReranking] = useState<boolean>(initSearch.enableReranking ?? false);

  // 드래그
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  const activeSources = getActiveSources(kb);

  // 문서 분류
  const fileDocs = documents.filter(d => !d.s3Key || (!d.s3Key.startsWith('web-crawl/') && d.s3Key.startsWith('documents/')));
  const webDocs = documents.filter(d => d.s3Key && d.s3Key.startsWith('web-crawl/'));

  // ─────────────────────────────────────
  // 드래그 핸들러
  // ─────────────────────────────────────
  const onMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('input, select, textarea, button')) return;
    setDragging(true);
    dragStart.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const newX = e.clientX - dragStart.current.x;
      const newY = e.clientY - dragStart.current.y;
      // 모달이 뷰포트 밖으로 나가지 않도록 제한
      const maxX = window.innerWidth * 0.4;
      const maxY = window.innerHeight * 0.3;
      setPos({
        x: Math.max(-maxX, Math.min(maxX, newX)),
        y: Math.max(-maxY, Math.min(maxY, newY)),
      });
    };
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [dragging]);

  // ─────────────────────────────────────
  // 데이터 로드
  // ─────────────────────────────────────
  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const docs = await knowledgeBaseApi.getDocuments(kb.id);
      setDocuments(docs);
    } catch {
      showToast('error', '문서 로드 실패');
    } finally {
      setLoading(false);
    }
  }, [kb.id, showToast]);

  const refreshKb = useCallback(async () => {
    try { setKb(await knowledgeBaseApi.get(kb.id)); } catch { /* ignore */ }
  }, [kb.id]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  // 모달 오픈 시 최신 KB를 다시 불러와 저장된 설정을 반영
  useEffect(() => {
    refreshKb();
  }, [refreshKb]);

  // kb가 갱신되면 저장된 검색 설정을 슬라이더에 동기화
  useEffect(() => {
    const s = (kb.pipelineConfig as any)?.search || {};
    if (typeof s.topK === 'number') setTopK(s.topK);
    if (typeof s.similarityThreshold === 'number') setSimilarityThreshold(s.similarityThreshold);
    if (s.searchType) setSearchType(s.searchType);
  }, [kb]);

  // ─────────────────────────────────────
  // 변경 감지
  // ─────────────────────────────────────
  const hasInfoChange = editName !== kb.name || editDesc !== (kb.description || '');
  const hasWebChange = activeSources.includes('web') && (
    webUrl !== (webCfg?.url || '') ||
    webDepth !== (webCfg?.depth || 2) ||
    webPageType !== (webCfg?.pageType || 'query') ||
    webStartPage !== (webCfg?.startPage || 1) ||
    webEndPage !== (webCfg?.endPage || 1)
  );
  const hasPipelineChange =
    embeddingModelId !== initEmbedding ||
    chunkStrategy !== (initChunking.strategy || 'fixed') ||
    chunkSize !== (initChunking.chunkSize || 500) ||
    chunkOverlap !== (initChunking.chunkOverlap ?? defaultOverlap) ||
    topK !== (initSearch.topK ?? 5) ||
    similarityThreshold !== (initSearch.similarityThreshold ?? 0.7) ||
    searchType !== (initSearch.searchType ?? 'semantic') ||
    enableReranking !== (initSearch.enableReranking ?? false);

  const hasChange = hasInfoChange || hasWebChange || hasPipelineChange || dbEditOpen;

  // 임베딩 모델 변경 시 재인덱싱 필요 여부
  const needsReindex = embeddingModelId !== initEmbedding || chunkStrategy !== (initChunking.strategy || 'fixed') || chunkSize !== (initChunking.chunkSize || 500);

  // ─────────────────────────────────────
  // 저장
  // ─────────────────────────────────────
  const handleSave = async () => {
    if (!editName.trim()) return showToast('error', '이름을 입력하세요');
    if (activeSources.includes('web') && !webUrl.trim()) return showToast('error', 'URL을 입력하세요');

    setSaving(true);
    try {
      const cfg = kb.pipelineConfig as any;
      const newWebCfg = {
        url: webUrl.trim(),
        depth: webDepth,
        pageType: webPageType,
        startPage: parseInt(webStartPage) || 1,
        endPage: parseInt(webEndPage) || 1,
        pageParam: webPageParam.trim() || (webPageType === 'path' ? 'page' : 'currentPage'),
      };

      let sources = cfg?.sources;
      if (sources && activeSources.includes('web')) {
        sources = sources.map((s: any) => s.type === 'web' ? { ...s, ...newWebCfg } : s);
      }
      // DB 설정 변경 반영
      if (sources && activeSources.includes('database')) {
        sources = sources.map((s: any) => s.type === 'database' ? {
          ...s,
          syncSchedule: dbConfig.syncSchedule,
          hints: dbConfig.hints,
          sqlSystemPrompt: dbConfig.sqlSystemPrompt,
          sqlSummaryPrompt: dbConfig.sqlSummaryPrompt,
          sampleRows: dbConfig.sampleRows,
          tables: dbConfig.tables,
        } : s);
      }

      const newPipelineConfig = {
        ...cfg,
        ...(sources && { sources }),
        webConfig: activeSources.includes('web') ? newWebCfg : cfg?.webConfig,
        embeddingModelId,
        chunking: { strategy: chunkStrategy, chunkSize, chunkOverlap, respectSentenceBoundary: true },
        search: { topK, similarityThreshold, enableReranking, searchType },
      };

      await knowledgeBaseApi.update(kb.id, {
        name: editName.trim(),
        description: editDesc.trim() || undefined,
        ...(editCustomerId ? { customerIdOverride: editCustomerId } : {}),
        ...(editTeamId ? { teamIdOverride: editTeamId } : {}),
        pipelineConfig: newPipelineConfig as any,
        // 파이프라인 설정 변경 시 재인덱싱 필요 표시
        ...(needsReindex && { status: 'needs-sync' as const }),
      });
      setKb(await knowledgeBaseApi.get(kb.id));
      showToast('success', needsReindex ? '저장됨 — 동기화 버튼을 눌러 재인덱싱하세요.' : '저장됨');
      onUpdate();
    } catch {
      showToast('error', '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────
  // 동기화 + 진행상황 폴링
  // ─────────────────────────────────────
  const stopPoll = () => {
    if (syncPollRef.current) { clearInterval(syncPollRef.current); syncPollRef.current = null; }
  };

  const startPoll = () => {
    stopPoll();
    syncPollRef.current = setInterval(async () => {
      try {
        const docs = await knowledgeBaseApi.getDocuments(kb.id);
        setDocuments(docs);
        const indexed    = docs.filter(d => d.status === 'indexed').length;
        const processing = docs.filter(d => d.status === 'processing' || d.status === 'pending').length;
        const error      = docs.filter(d => d.status === 'error').length;
        setSyncProgress({ total: docs.length, indexed, processing, error });

        const latestKb = await knowledgeBaseApi.get(kb.id);
        setKb(latestKb);

        // 동기화 완료 감지
        if (latestKb.status !== 'syncing' && latestKb.status !== 'indexing' && processing === 0) {
          stopPoll();
          setSyncing(false);
          setSyncProgress(null);
          onUpdate();
        }
      } catch { /* ignore */ }
    }, 2000);
  };

  useEffect(() => () => stopPoll(), []); // 언마운트 시 정리

  const handleSync = async () => {
    setSyncing(true);
    setSyncProgress({ total: 0, indexed: 0, processing: 0, error: 0 });
    try {
      await knowledgeBaseApi.sync(kb.id);
      showToast('success', '동기화 시작됨');
      startPoll();
    } catch {
      showToast('error', '동기화 실패');
      setSyncing(false);
      setSyncProgress(null);
      await refreshKb();
    }
  };

  // ─────────────────────────────────────
  // 파일 업로드/삭제
  // ─────────────────────────────────────
  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) await knowledgeBaseApi.uploadDocument(kb.id, f);
      showToast('success', `${files.length}개 업로드됨 — 처리 중...`);
      await loadDocs();
      onUpdate();
      // 업로드 후 처리 완료까지 자동 폴링 시작
      startPoll();
    } catch {
      showToast('error', '업로드 실패');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (docId: string) => {
    if (!confirm('삭제하시겠습니까?')) return;
    setDeletingId(docId);
    try {
      await knowledgeBaseApi.deleteDocument(kb.id, docId);
      showToast('success', '삭제됨');
      await loadDocs();
      onUpdate();
    } catch {
      showToast('error', '삭제 실패');
    } finally {
      setDeletingId(null);
    }
  };

  // ─────────────────────────────────────
  // 상태 뱃지
  // ─────────────────────────────────────
  const StatusBadge = ({ status, dot = false }: { status: string; dot?: boolean }) => {
    const map: Record<string, { color: BadgeColor; txt: string }> = {
      indexed:    { color: 'success', txt: '완료' },
      processing: { color: 'medium',  txt: '처리 중' },
      pending:    { color: 'medium',  txt: '대기' },
      error:      { color: 'error',   txt: '오류' },
    };
    const { color, txt } = map[status] || map.pending;
    return <Badge color={color} dot={dot}>{txt}</Badge>;
  };

  // ─────────────────────────────────────
  // 렌더링
  // ─────────────────────────────────────
  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      // 제목 + 수집 건수 · 마지막 동기화
      title={
        <div className="km-title">
          <div className="km-title-text">지식베이스 관리</div>
          <div className="km-title-meta">
            <span>수집 {loading ? '...' : `${documents.length > 0 ? documents.length : kb.documentCount}건`}</span>
            {kb.lastSyncedAt && (
              <>
                <span className="km-title-dot">·</span>
                <span>마지막 동기화 {new Date(kb.lastSyncedAt).toLocaleString()}</span>
              </>
            )}
          </div>
        </div>
      }
      // 기존 동작 유지: 바깥 클릭 · ✕ 로 닫힘, ESC 로는 안 닫힘
      closeOnEsc={false}
      className="km-modal"
      style={{ transform: `translate(${pos.x}px, ${pos.y}px)` }}
      // 헤더 = 드래그 핸들
      headerProps={{ className: 'km-header', onMouseDown }}
      // 헤더: ✕ 옆 새로고침
      headerActions={
        <button
          className={`btn-refresh${loading ? ' spinning' : ''} modal-refresh`}
          onClick={loadDocs}
          disabled={loading}
          title="새로고침"
        >
          {Icons.refresh}<span>새로고침</span>
        </button>
      }
      footerRight={
        <>
          <Button appearance="outline" color="gray" size='md' onClick={onClose}>닫기</Button>
          <Button onClick={handleSave} disabled={saving || !hasChange}>
            {saving ? '저장 중...' : '저장'}
          </Button>
        </>
      }
    >
        {/* 콘텐츠 (스크롤·여백은 공통 .dialog-body) */}
        <>

          {/* 동기화 필요 배너 */}
          {kb.status === 'needs-sync' && !syncing && (
            <Notice
              color="warning"
              title="파이프라인 설정이 변경되었습니다. 동기화를 실행해야 반영됩니다."
              action={
                <Button color="gray" appearance="outline" size="sm" onClick={handleSync}>
                  <RefreshIcon /> 지금 동기화
                </Button>
              }
            >
            </Notice>
          )}

          {/* 동기화 진행 배너 */}
          {syncing && (
            <Notice
              color="info"
              loading
              title="동기화 진행 중..."
              action={syncProgress && syncProgress.total > 0 && (
                <span className="km-sync-progress-stats">
                  {syncProgress.indexed}/{syncProgress.total} 완료
                  {syncProgress.processing > 0 && ` · ${syncProgress.processing}개 처리 중`}
                  {syncProgress.error > 0 && ` · ${syncProgress.error}개 오류`}
                </span>
              )}
            >
              {syncProgress && syncProgress.total > 0 && (
                <div className="km-sync-progress-bar-wrap">
                  <div
                    className="km-sync-progress-bar"
                    style={{ width: `${Math.round((syncProgress.indexed / syncProgress.total) * 100)}%` }}
                  />
                </div>
              )}
            </Notice>
          )}

          {/* 기본 정보 */}
          <section className="km-section">
            <div className="km-form-grid">
              <div className="form-item">
                <label className='form-label'>이름</label>
                <input value={editName} onChange={e => setEditName(e.target.value)} />
              </div>
              <div className="form-item">
                <div className='form-label'>
                  설명
                  <div className='optional'>(선택)</div>
                </div>
                <input value={editDesc} onChange={e => setEditDesc(e.target.value)} placeholder="" />
              </div>
              {/* 고객사 선택 (productAdmin/tenantAdmin) */}
              {needsCustomerSelect && (
                <div className="form-item">
                  <label className='form-label'>고객사</label>
                  <Select value={editCustomerId} onChange={e => { setEditCustomerId(e.target.value); setEditTeamId(''); }}>
                    <option value="">고객사 선택 (선택)</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Select>
                </div>
              )}
              {/* 팀 선택 (productAdmin/tenantAdmin/customerAdmin) */}
              {needsTeamSelect && (
                <div className="form-item">
                  <label className='form-label'>팀</label>
                  <Select
                    value={editTeamId}
                    onChange={e => setEditTeamId(e.target.value)}
                    disabled={needsCustomerSelect && !editCustomerId}
                  >
                    <option value="">
                      {needsCustomerSelect && !editCustomerId ? '고객사를 먼저 선택하세요' : '팀 선택 (선택)'}
                    </option>
                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </Select>
                </div>
              )}
            </div>
          </section>

          {/* 데이터 소스 */}
          <section className="km-section">
            <h3>데이터 소스</h3>
            <div className="km-source-cards">

              {/* 파일 소스 */}
              {activeSources.includes('file') && (
                <div className="km-card km-card-file-extended">
                  <div className="km-card-header file">
                    <span className="km-card-title">파일 <span className='km-card-subtitle'>(PDF, TXT, MD, Excel, 이미지(PNG/JPG)) (최대 50MB)</span></span>
                    <span className="km-card-count">{fileDocs.length}개</span>
                  </div>
                  
                  {/* 파일 추가 탭 (로컬/Drive) - 상단 */}
                  <div className="km-file-tabs-wrap">
                    <div className="pipe-file-source-tabs">
                      <button
                        className={`pipe-file-source-tab${fileTab === 'local' ? ' active' : ''}`}
                        onClick={() => setFileTab('local')}
                      >
                        <LocalIcon /> 로컬 파일
                      </button>
                      <button
                        className={`pipe-file-source-tab${fileTab === 'drive' ? ' active' : ''}`}
                        onClick={() => {
                          setFileTab('drive');
                          if (driveConnected === null) {
                            connectorApi.getKbGoogleDriveConfig().then(cfg => {
                              setDriveConnected(!!cfg?.connected);
                              setDriveAccount(cfg?.connectedEmail || '');
                            }).catch(() => setDriveConnected(false));
                          }
                        }}
                      >
                        <img src={googleDriveLogo} alt="" className="km-drive-icon" /> Google Drive
                      </button>
                      <button
                        className={`pipe-file-source-tab${fileTab === 'dropbox' ? ' active' : ''}`}
                        onClick={() => {
                          setFileTab('dropbox');
                          if (dropboxConnected === null) {
                            connectorApi.getKbDropboxConfig().then(cfg => {
                              setDropboxConnected(!!cfg?.connected);
                              setDropboxAccount(cfg?.connectedEmail || '');
                            }).catch(() => setDropboxConnected(false));
                          }
                        }}
                      >
                        <img src={dropboxLogo} alt="" className="km-drive-icon" /> Dropbox
                      </button>
                    </div>

                    {/* 로컬 파일 추가 */}
                    {fileTab === 'local' && (
                      <div className="km-file-upload km-file-tab-local">
                        <input ref={fileInputRef} type="file" multiple accept=".pdf,.txt,.md,.xlsx,.xls,.docx,.png,.jpg,.jpeg" onChange={handleUpload} hidden />
                        <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                          {uploading ? '업로드 중...' : '파일 선택'}
                        </Button>
                        <div className="km-file-hint">PDF · TXT · MD · Excel · 이미지(PNG/JPG)</div>
                      </div>
                    )}

                    {/* Google Drive 파일 추가 */}
                    {fileTab === 'drive' && (
                      <div className={`km-file-upload${driveConnected === true ? ' km-file-upload--drive' : ''}`}>
                        {driveConnected === null && (
                          <div className="km-card-empty">연결 상태 확인 중...</div>
                        )}

                        {driveConnected === false && (
                          <div className="">
                            <Button size="sm" onClick={async () => {
                              try {
                                const cfg = await connectorApi.getKbGoogleDriveConfig();
                                if (!cfg?.clientId) {
                                  setDriveError('지식베이스용 Google Drive Client ID가 설정되지 않았습니다. 시스템 설정에서 설정해주세요.');
                                  return;
                                }
                                const url = await connectorApi.getKbGoogleDriveAuthUrl();
                                const popup = window.open(url, 'kb-google-drive-auth', 'width=500,height=600');
                                const timer = setInterval(async () => {
                                  if (popup?.closed) {
                                    clearInterval(timer);
                                    const saved = await connectorApi.getKbGoogleDriveConfig();
                                    setDriveConnected(!!saved?.connected);
                                    setDriveAccount(saved?.connectedEmail || '');
                                  }
                                }, 500);
                              } catch {
                                setDriveError('인증 URL을 가져오는데 실패했습니다.');
                              }
                            }}>
                              Google 계정 연결
                            </Button>
                            {driveError && <Notice color="error">{driveError}</Notice>}
                          </div>
                        )}

                        {driveConnected === true && (
                          <>
                            {/* 연결 계정 - 공통 연결 카드 (styles/workflow.css .node-account-*) */}
                            <div className="node-account-card node-account-card--logo bedrock-guide-step">
                              <div className="node-account-info">
                                <img src={googleDriveLogo} alt="" className="node-account-logo" />
                                <div className="node-account-text">
                                  <strong>{driveAccount}</strong>
                                  <span>Google Drive 계정이 연결되어 있습니다</span>
                                </div>
                              </div>
                              <div className="node-account-actions">
                                <Button color="gray" appearance="outline" size="sm" onClick={async () => {
                                  try {
                                    const url = await connectorApi.getKbGoogleDriveAuthUrl();
                                    const popup = window.open(url, 'kb-google-drive-auth', 'width=500,height=600');
                                    const timer = setInterval(async () => {
                                      if (popup?.closed) {
                                        clearInterval(timer);
                                        const saved = await connectorApi.getKbGoogleDriveConfig();
                                        setDriveConnected(!!saved?.connected);
                                        setDriveAccount(saved?.connectedEmail || '');
                                      }
                                    }, 500);
                                  } catch {}
                                }}>
                                  <RefreshIcon /> 연결 설정
                                </Button>
                              </div>
                            </div>

                            {/* 폴더 입력 + 파일 조회 - 한 줄 전체 폭 */}
                            <div className="km-drive-folder-row">
                              <div className="input">
                                <input
                                  type="text"
                                  className="input-control"
                                  placeholder="폴더 URL 또는 ID (비우면 내 드라이브 전체)"
                                  value={driveFolderId}
                                  onChange={e => setDriveFolderId(e.target.value)}
                                />
                              </div>
                              <Button color="primary" onClick={async () => {
                                setDriveLoading(true);
                                setDriveError('');
                                try {
                                  const list = await connectorApi.listKbGoogleDriveFiles(driveFolderId || undefined);
                                  setDriveFileList(list);
                                } catch {
                                  setDriveError('파일 목록을 불러오는데 실패했습니다.');
                                } finally {
                                  setDriveLoading(false);
                                }
                              }} disabled={driveLoading}>
                                {driveLoading ? '조회 중...' : '파일 조회'}
                              </Button>
                            </div>
                            {driveError && <Notice color="error">{driveError}</Notice>}
                            
                            {driveFileList.length > 0 && (
                              <div className="km-card-list file-list km-drive-file-list">
                                {driveFileList.map(f => (
                                  <div key={f.id} className="file-item">
                                    <div className="file-item-info">
                                      <span className="file-item-icon">{f.mimeType?.includes('folder') ? <FolderIcon /> : Icons.file}</span>
                                      <span className="file-item-name" title={f.name}>{f.name}</span>
                                    </div>
                                    <div className="file-item-meta">{f.size ? formatSize(f.size) : '-'}</div>
                                    <Button size="sm" appearance="outline" disabled={driveImporting} onClick={async () => {
                                      if (driveImporting) return;
                                      setDriveImporting(true);
                                      try {
                                        await connectorApi.importKbDriveFileToKb(kb.id, f.id, f.name);
                                        showToast('success', `${f.name} 가져오기 완료`);
                                        const docs = await knowledgeBaseApi.getDocuments(kb.id);
                                        setDocuments(docs);
                                      } catch (err: any) {
                                        showToast('error', err?.message || '파일 가져오기 실패');
                                      } finally {
                                        setDriveImporting(false);
                                      }
                                    }}>
                                      {driveImporting ? '...' : '가져오기'}
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  {/* Dropbox 파일 추가 */}
                  {fileTab === 'dropbox' && (
                    <div className={`km-file-upload${dropboxConnected === true ? ' km-file-upload--drive' : ''}`}>
                      {dropboxConnected === null && <div className="km-card-empty">연결 상태 확인 중...</div>}
                      {dropboxConnected === false && (
                        <div>
                          <Button size="sm" onClick={async () => {
                            try {
                              const cfg = await connectorApi.getKbDropboxConfig();
                              if (!cfg?.clientId) { setDropboxError('Dropbox App Key가 설정되지 않았습니다. 시스템 설정에서 먼저 설정해주세요.'); return; }
                              const url = await connectorApi.getKbDropboxAuthUrl();
                              const popup = window.open(url, 'kb-dropbox-auth', 'width=500,height=700');
                              const timer = setInterval(async () => {
                                if (popup?.closed) {
                                  clearInterval(timer);
                                  const saved = await connectorApi.getKbDropboxConfig();
                                  setDropboxConnected(!!saved?.connected);
                                  setDropboxAccount(saved?.connectedEmail || '');
                                }
                              }, 500);
                            } catch (e) { setDropboxError('인증 URL을 가져오는데 실패했습니다.'); }
                          }}>Dropbox 계정 연결</Button>
                          {dropboxError && <Notice color="error">{dropboxError}</Notice>}
                        </div>
                      )}
                      {dropboxConnected === true && (
                        <>
                          <div className="node-account-card node-account-card--logo bedrock-guide-step">
                            <div className="node-account-info">
                              <img src={dropboxLogo} alt="" className="node-account-logo" />
                              <div className="node-account-text">
                                <strong>{dropboxAccount}</strong>
                                <span>Dropbox 계정이 연결되어 있습니다</span>
                              </div>
                            </div>
                            <div className="node-account-actions">
                              <Button color="gray" appearance="outline" size="sm" onClick={async () => {
                                try {
                                  const url = await connectorApi.getKbDropboxAuthUrl();
                                  const popup = window.open(url, 'kb-dropbox-auth', 'width=500,height=700');
                                  const timer = setInterval(async () => {
                                    if (popup?.closed) { clearInterval(timer); const s = await connectorApi.getKbDropboxConfig(); setDropboxConnected(!!s?.connected); setDropboxAccount(s?.connectedEmail || ''); }
                                  }, 500);
                                } catch (e2) { /* ignore */ }
                              }}><RefreshIcon /> 연결 설정</Button>
                            </div>
                          </div>
                          <div className="km-drive-folder-row">
                            <div className="input">
                              <input type="text" className="input-control"
                                placeholder="폴더 경로 (비우면 전체 Dropbox)"
                                value={dropboxFolderPath}
                                onChange={e => setDropboxFolderPath(e.target.value)}
                              />
                            </div>
                            <Button color="primary" disabled={dropboxLoading} onClick={async () => {
                              setDropboxLoading(true); setDropboxError('');
                              try {
                                const list = await connectorApi.listKbDropboxFiles(dropboxFolderPath || undefined);
                                setDropboxFileList(list);
                              } catch (e3) { setDropboxError('파일 목록을 불러오는데 실패했습니다.'); }
                              setDropboxLoading(false);
                            }}>{dropboxLoading ? '조회 중...' : '파일 조회'}</Button>
                          </div>
                          {dropboxError && <Notice color="error">{dropboxError}</Notice>}
                          {dropboxFileList.length > 0 && (
                            <div className="km-card-list file-list km-drive-file-list">
                              {dropboxFileList.map(f => (
                                <div key={f.id} className="file-item">
                                  <div className="file-item-info">
                                    <span className="file-item-icon">{Icons.file}</span>
                                    <span className="file-item-name" title={f.name}>{f.name}</span>
                                  </div>
                                  <div className="file-item-meta">{f.size ? formatSize(f.size) : '-'}</div>
                                  <Button size="sm" appearance="outline" disabled={dropboxImporting} onClick={async () => {
                                    if (dropboxImporting) return;
                                    setDropboxImporting(true);
                                    try {
                                      await connectorApi.importKbDropboxFileToKb(kb.id, f.id, f.name);
                                      showToast('success', `${f.name} 가져오기 완료`);
                                      const docs = await knowledgeBaseApi.getDocuments(kb.id);
                                      setDocuments(docs);
                                    } catch (e4: any) { showToast('error', e4?.message || '파일 가져오기 실패'); }
                                    setDropboxImporting(false);
                                  }}>{dropboxImporting ? '...' : '가져오기'}</Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  </div>

                  {/* 업로드된 파일 목록 - 하단 */}
                  {fileDocs.length > 0 ? (
                    <div className="km-file-list-section">
                      <div className='km-file-list-label'>업로드된 파일</div>
                      {fileDocs.slice(0, 5).map(d => (
                        <div key={d.id} className="file-item pipe-file-item">
                          <div className="file-item-info">
                            <span className="file-item-icon">{Icons.file}</span>
                            <span className="file-item-name" title={d.name}>{d.name}</span>
                          </div>
                          <div className="file-item-meta">{formatSize(d.size)}</div>
                          <StatusBadge status={d.status} />
                          <button
                            className="file-item-remove"
                            onClick={() => handleDelete(d.id)}
                            disabled={deletingId === d.id}
                            title="삭제"
                            aria-label={`${d.name} 삭제`}
                          >
                            {deletingId === d.id ? '...' : Icons.delete}
                          </button>
                        </div>
                      ))}
                      {fileDocs.length > 5 && <div className="km-card-more">+{fileDocs.length - 5}개 더</div>}
                    </div>
                  ) : (
                    <div className="km-card-empty km-file-list-section">업로드된 파일 없음</div>
                  )}
                </div>
              )}

              {/* 웹 소스 */}
              {activeSources.includes('web') && (
                <div className="km-card">
                  <div className="km-card-header web">
                    {/* <span className="km-card-icon">{Icons.web}</span> */}
                    <span className="km-card-title">웹 크롤링</span>
                    <span className="km-card-count">{loading ? '...' : (webDocs.length > 0 ? webDocs.length : kb.documentCount)}페이지</span>
                    <button className="km-card-btn btn-refresh" onClick={handleSync} disabled={syncing} title="재크롤링">
                      {syncing ? '...' : Icons.refresh}
                      <span>{syncing ? '동기화 중...' : '동기화'}</span>
                    </button>
                  </div>
                  <div className="km-card-config">
                    <div className="km-config-row">
                      <label>URL</label>
                      <input value={webUrl} onChange={e => setWebUrl(e.target.value)} placeholder="https://..." />
                    </div>
                    <div className="km-config-row km-config-inline">
                      <div>
                        <label>깊이</label>
                        <select value={webDepth} onChange={e => setWebDepth(Number(e.target.value))}>
                          <option value={1}>1</option>
                          <option value={2}>2</option>
                          <option value={3}>3</option>
                          <option value={5}>5</option>
                        </select>
                      </div>
                      <div>
                        <label>타입</label>
                        <select value={webPageType} onChange={e => setWebPageType(e.target.value as 'query' | 'path')}>
                          <option value="query">쿼리</option>
                          <option value="path">경로</option>
                        </select>
                      </div>
                      <div>
                        <label>페이지</label>
                        <div className="km-page-range">
                          <input type="number" value={webStartPage} onChange={e => setWebStartPage(e.target.value)} onBlur={() => { const num = parseInt(webStartPage); if (isNaN(num) || num < 1) setWebStartPage('1'); }} min={1} />
                          <span>~</span>
                          <input type="number" value={webEndPage} onChange={e => setWebEndPage(e.target.value)} onBlur={() => { const num = parseInt(webEndPage); if (isNaN(num) || num < 1) setWebEndPage('1'); }} min={1} />
                        </div>
                      </div>
                    </div>
                    {kb.status === 'error' && kb.errorMessage && (
                      <div className="km-config-error">{kb.errorMessage}</div>
                    )}
                  </div>
                  {webDocs.length > 0 && (
                    <div className="km-card-list">
                      {webDocs.slice(0, 3).map(d => (
                        <div key={d.id} className="km-card-item">
                          <span className="km-item-name">{d.name}</span>
                          <span className="km-item-meta">{d.chunkCount || 0}청크</span>
                          <StatusBadge status={d.status} />
                        </div>
                      ))}
                      {webDocs.length > 3 && <div className="km-card-more">+{webDocs.length - 3}페이지 더</div>}
                    </div>
                  )}
                </div>
              )}

              {/* DB 소스 */}
              {activeSources.includes('database') && (
                <div className="km-card km-db-card">
                  {(() => {
                    const db = getDbConfig(kb);
                    const dbDocs = documents.filter(d => d.s3Key?.startsWith('db-source/'));
                    const selectedTables = db?.tables?.filter((t: any) => t.selected !== false) ?? [];
                    // s3Key: db-source/{kbId}/{schema}.{table}.schema.md | .hint_N.md | .txt
                    const docInfo = (d: Document) => {
                      const file = d.s3Key?.split('/').pop() ?? '';
                      const kind = file.endsWith('.schema.md') ? '스키마' : /\.hint_\d+\.md$/.test(file) ? '힌트' : '데이터';
                      const table = file.replace(/\.(schema\.md|hint_\d+\.md|txt)$/, '').split('.').pop() || d.name;
                      return { kind, table };
                    };
                    const totalChunks = dbDocs.reduce((sum, d) => sum + (d.chunkCount || 0), 0);

                    // 테이블+유형별 그룹핑 (힌트는 묶어서 1행)
                    const groupedRows = (() => {
                      const map = new Map<string, { table: string; kind: string; chunkCount: number; hintCount: number; status: string }>();
                      dbDocs.forEach(d => {
                        const { kind, table } = docInfo(d);
                        const key = `${table}__${kind}`;
                        if (map.has(key)) {
                          const row = map.get(key)!;
                          row.chunkCount += d.chunkCount || 0;
                          if (kind === '힌트') row.hintCount += 1;
                          if (d.status === 'error') row.status = 'error';
                        } else {
                          map.set(key, {
                            table,
                            kind,
                            chunkCount: d.chunkCount || 0,
                            hintCount: kind === '힌트' ? 1 : 0,
                            status: d.status || 'pending',
                          });
                        }
                      });
                      return Array.from(map.values());
                    })();
                    return (
                      <>
                        {/* 헤더: 아이콘 · 이름 + DB 종류 · 접속 주소 / 설정 편집 · 동기화 */}
                        <div className="km-db-head">
                          <span className="km-db-icon">{Icons.database}</span>
                          <div className="km-db-head-info">
                            <div className="km-db-head-title">
                              <span>DB 커넥터</span>
                              {db && <Badge variant="type">{(db.dbType || db.type || 'mysql').toUpperCase()}</Badge>}
                            </div>
                            <div className="km-db-head-addr">
                              {db ? `${db.host}:${db.port}/${db.database}` : '설정 없음'}
                            </div>
                          </div>
                          <div className="km-db-head-actions">
                            <button className='btn-refresh' onClick={handleSync} disabled={syncing} title="동기화">
                              {Icons.refresh}
                              <span>{syncing ? '동기화 중...' : '동기화'}</span>
                            </button>
                            <Button color="primary" appearance="fill" size='sm' onClick={() => setDbEditOpen(o => !o)} title="DB 설정 편집">
                              {/* <EditIcon /> */}
                              <span>{dbEditOpen ? '편집 닫기' : '설정 편집'}</span>
                            </Button>
                          </div>
                        </div>

                        {/* 수집 테이블 */}
                        {(dbDocs.length > 0 || selectedTables.length > 0) && (
                          <div className="table-wrap">
                            <table>
                              <colgroup>
                                <col />
                                <col width={100}/>
                                <col width={100}/>
                                <col width={100}/>
                              </colgroup>
                              <thead>
                                <tr>
                                  <th className="left">수집 테이블</th>
                                  <th>유형</th>
                                  <th>청크</th>
                                  <th>상태</th>
                                </tr>
                              </thead>
                              <tbody>
                                {dbDocs.length > 0
                                  ? groupedRows.map((row, i) => (
                                      <tr key={i}>
                                        <td className="left km-db-table-name">{row.table}</td>
                                        <td>
                                          {/* 문서 종류 배지 - 스키마 info / SQL 힌트 secondary / 데이터 success */}
                                          {row.kind === '스키마'
                                            ? <Badge color="info" size="sm">스키마</Badge>
                                            : row.kind === '힌트'
                                              ? <Badge color="secondary" size="sm">SQL 힌트</Badge>
                                              : <Badge color="success" size="sm">데이터</Badge>}
                                        </td>
                                        <td title={row.kind === '힌트' ? `${row.hintCount}개 힌트, ${row.chunkCount}청크` : `${row.chunkCount}청크`}>
                                          {row.kind === '힌트' ? `${row.hintCount}건` : `${row.chunkCount}청크`}
                                        </td>
                                        <td><StatusBadge status={row.status} dot /></td>
                                      </tr>
                                    ))
                                  : selectedTables.map((t: any) => (
                                      <tr key={`${t.schema}.${t.table}`}>
                                        <td className="left km-db-table-name">{t.table}</td>
                                        <td>{t.columns?.length || 0}컬럼</td>
                                        <td>-</td>
                                        <td><StatusBadge status="pending" dot /></td>
                                      </tr>
                                    ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {kb.status === 'error' && kb.errorMessage && (
                          <div className="km-config-error">{kb.errorMessage}</div>
                        )}
                      </>
                    );
                  })()}
                  {/* 인라인 DB 설정 편집 */}
                  {dbEditOpen && (
                    <div className="km-db-edit-area">
                      <DatabaseSourceStep config={dbConfig} onChange={setDbConfig} isEditing={true} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* 임베딩 모델 (라디오 카드) */}
          <section className="km-section">
            <h3>임베딩 모델</h3>
                <div className="choice-cards" role="radiogroup" aria-label="임베딩 모델">
                  {EMBEDDING_MODELS.map(m => (
                    <button
                      key={m.id}
                      type="button"
                      role="radio"
                      aria-checked={embeddingModelId === m.id}
                      className={`choice-card${embeddingModelId === m.id ? ' active' : ''}`}
                      onClick={() => setEmbeddingModelId(m.id)}
                    >
                      <span className="choice-card-radio" />
                      <span className="choice-card-body">
                        <span className="choice-card-title">{m.name}</span>
                        <span className="choice-card-desc">{m.provider}</span>
                        <span className="choice-card-tags">
                          <Badge variant="type">{m.dimensions}차원</Badge>
                          <Badge variant="type">최대 입력 {m.maxTokens.toLocaleString()}토큰</Badge>
                          <Badge variant="type">${m.costPer1kTokens} / 1K</Badge>
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
                {embeddingModelId !== initEmbedding && (
                  <div className="km-warn">⚠ 임베딩 모델 변경 시 저장 후 동기화하면 전체 재인덱싱됩니다.</div>
                )}
          </section>

          {/* 청킹 설정 */}
          <section className="km-section">
            <h3>청킹 설정</h3>
            <div className="km-pipeline-row">
              <div className="km-field">
                <label>전략</label>
                <select value={chunkStrategy} onChange={e => setChunkStrategy(e.target.value)}>
                  {CHUNK_STRATEGIES.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              {chunkStrategy !== 'semantic' && (
                <>
                  <div className="km-field">
                    <label>청크 크기 (토큰)</label>
                    <input
                      type="number"
                      value={chunkSize}
                      min={100} max={2000}
                      onChange={e => setChunkSize(parseInt(e.target.value) || 500)}
                    />
                  </div>
                  {!isDbSource && (
                    <div className="km-field">
                      <label>오버랩 (토큰)</label>
                      <input
                        type="number"
                        value={chunkOverlap}
                        min={0} max={500}
                        onChange={e => setChunkOverlap(parseInt(e.target.value) || 0)}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            {(chunkStrategy !== initChunking.strategy || chunkSize !== initChunking.chunkSize) && (
              <div className="km-warn">⚠ 청킹 변경 시 저장 후 동기화하면 전체 재인덱싱됩니다.</div>
            )}
          </section>

          {/* 검색 설정 */}
          <section className="km-section">
            <h3>검색 설정</h3>

            {/* 검색 유형 (라디오 카드) */}
            <div className="km-field mb-20">
                <div className="choice-cards" role="radiogroup" aria-label="검색 유형">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={searchType === 'semantic'}
                    className={`choice-card${searchType === 'semantic' ? ' active' : ''}`}
                    onClick={() => setSearchType('semantic')}
                  >
                    <span className="choice-card-radio" />
                    <span className="choice-card-body">
                      <span className="choice-card-title">시맨틱 검색</span>
                      <span className="choice-card-desc">순수 벡터 검색. 유사도 0~1 임계값이 정확하게 동작합니다.</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={searchType === 'hybrid'}
                    className={`choice-card${searchType === 'hybrid' ? ' active' : ''}`}
                    onClick={() => setSearchType('hybrid')}
                  >
                    <span className="choice-card-radio" />
                    <span className="choice-card-body">
                      <span className="choice-card-title">하이브리드 검색</span>
                      <span className="choice-card-desc">벡터 + 텍스트(BM25). 코드·고유명사 같은 키워드 검색에 강합니다.</span>
                    </span>
                  </button>
                </div>
            </div>

            <div className="km-pipeline-row">
              <div className="km-field">
                <label>Top K (검색 결과 수)</label>
                <div className="km-slider-row">
                  <input
                    type="range"
                    min={1} max={50}
                    value={topK}
                    onChange={e => setTopK(parseInt(e.target.value))}
                    style={{ '--fill': `${((topK - 1) / 49) * 100}%` } as React.CSSProperties}
                  />
                  <span className="km-slider-val">{topK}</span>
                </div>
              </div>
              <div className="km-field">
                <label>유사도 임계값</label>
                <div className="km-slider-row">
                  <input
                    type="range"
                    min={0} max={100}
                    value={Math.round(similarityThreshold * 100)}
                    onChange={e => setSimilarityThreshold(parseInt(e.target.value) / 100)}
                    style={{ '--fill': `${Math.round(similarityThreshold * 100)}%` } as React.CSSProperties}
                  />
                  <span className="km-slider-val">{(similarityThreshold * 100).toFixed(0)}%</span>
                </div>
              </div>
            </div>
            <div className="km-checkbox-row">
              <Checkbox
                checked={enableReranking}
                onChange={e => setEnableReranking(e.target.checked)}
                label="리랭킹 활성화 — 벡터 검색 후 AI가 결과를 재정렬합니다"
              />
            </div>
          </section>

          {/* 소유자 */}
          <section className="km-section km-owner">
            <h3>소유자</h3>
            <div className="km-owner-display">{kb.ownerName || '시스템'}</div>
          </section>

        </>
    </Modal>
  );
}

export default KnowledgeBaseManageModal;
